import type { BlogPostEntry, PostStatus } from "../api/contract";
import { pendingLine } from "./postChanges";

/** The words the blog area says around publishing, so each screen and the tests read the same. */

export interface Locale { zone?: string | undefined; language?: string | undefined }

/** "Mon 12 Oct, 9:00 am" or "21 Sep": the year only when it isn't this one. */
export function shortDate(iso: string | undefined, locale: Locale, options: { time?: boolean; weekday?: boolean; now?: number } = {}): string {
  const at = iso ? Date.parse(iso) : Number.NaN;
  if (Number.isNaN(at)) return "";
  const year = (time: number) => { try { return new Intl.DateTimeFormat("en", { year: "numeric", ...(locale.zone ? { timeZone: locale.zone } : {}) }).format(time); } catch { return new Date(time).getUTCFullYear().toString(); } };
  const shape: Intl.DateTimeFormatOptions = {
    day: "numeric", month: "short",
    ...(year(at) === year(options.now ?? Date.now()) ? {} : { year: "numeric" }),
    ...(options.weekday ? { weekday: "short" } : {}),
    ...(options.time ? { hour: "numeric", minute: "2-digit" } : {}),
  };
  for (const language of [locale.language, undefined]) {
    for (const timeZone of [locale.zone, undefined]) {
      try { return new Intl.DateTimeFormat(language, { ...shape, ...(timeZone ? { timeZone } : {}) }).format(at); } catch { /* an unknown language or zone: try the browser's */ }
    }
  }
  return "";
}

/** "tanah.stack.je/journal/ash-glazes" */
export function postAddress(address: string, base: string, slug: string): string {
  return [address.replace(/\/$/, ""), ...base.split("/").filter(Boolean), slug].filter(Boolean).join("/");
}

const DAY = 86_400_000;

/** When a post was last edited, for a draft's row: "Edited today", "Edited this week" or "Edited 21 Sep". */
export function editedLine(updatedAt: string | undefined, locale: Locale, now = Date.now()): string {
  const at = updatedAt ? Date.parse(updatedAt) : Number.NaN;
  if (Number.isNaN(at)) return "Draft";
  const sameDay = shortDate(updatedAt, locale, { now }) === shortDate(new Date(now).toISOString(), locale, { now });
  if (sameDay) return "Edited today";
  if (now - at < 7 * DAY) return "Edited this week";
  return `Edited ${shortDate(updatedAt, locale, { now })}`;
}

/** The second line of a post's row: when it was edited, when it goes live, or since when it has been. */
export function whenLine(post: Pick<BlogPostEntry, "status" | "updatedAt" | "firstPublishedAt"> & { live: Partial<BlogPostEntry["working"]> | null }, locale: Locale, now = Date.now()): string {
  if (post.status === "scheduled") return `Goes live ${shortDate(post.live?.publishAt, locale, { time: true, weekday: true, now })}`;
  if (post.status === "published") {
    const since = shortDate(post.live?.publishAt ?? post.firstPublishedAt ?? undefined, locale, { now });
    return since ? `Live since ${since}` : "Live";
  }
  return editedLine(post.updatedAt, locale, now);
}

export type PostFilter = "all" | PostStatus;

export interface PostCounts { all: number; draft: number; scheduled: number; published: number }

export function postCounts(posts: ReadonlyArray<Pick<BlogPostEntry, "status">>): PostCounts {
  const counts: PostCounts = { all: posts.length, draft: 0, scheduled: 0, published: 0 };
  for (const post of posts) counts[post.status] += 1;
  return counts;
}

/** The posts a filter and a search show: matching the title and summary, newest dated first. */
export function visiblePosts<T extends Pick<BlogPostEntry, "status" | "working">>(posts: readonly T[], filter: PostFilter, query: string): T[] {
  const needle = query.trim().toLowerCase();
  return posts
    .filter((post) => (filter === "all" || post.status === filter) && `${post.working.title} ${post.working.summary ?? ""}`.toLowerCase().includes(needle))
    .sort((a, b) => (b.working.publishAt ?? "").localeCompare(a.working.publishAt ?? ""));
}

/** Where a post shows besides its own page: the blog's list page, Home's latest posts when that's on, and each tag's page. */
export function shownOn(post: { tags: readonly string[] }, blog: { name: string; latestOnHome: boolean }, tagLabel: (id: string) => string | undefined): string[] {
  return [
    blog.name,
    ...(blog.latestOnHome ? ["Home · latest posts"] : []),
    ...post.tags.flatMap((id) => { const label = tagLabel(id); return label ? [`${label} tag`] : []; }),
  ];
}

/** The places a post shows, in a sentence: "Journal, Home's latest posts and the Glazes tag". */
export function placesSentence(places: readonly string[]): string {
  const words = places.map((place) => place === "Home · latest posts" ? "Home's latest posts" : place.endsWith(" tag") ? `the ${place}` : place);
  return words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

/** "Your website's 3 unpublished changes aren't included." The Website's own changes stay as they are. */
export function websiteChangesNote(count: number): string {
  if (count <= 0) return "Nothing else on your website changes.";
  return `Your website's ${count} unpublished ${count === 1 ? "change isn't" : "changes aren't"} included.`;
}

export type Confirmation = "publish" | "schedule" | "update" | "unpublish";

export interface ConfirmationCopy {
  title: string;
  /** Text around the web address, which is set apart: before it, and after it. */
  lead: { before: string; address: string; after: string };
  /** Where the post shows besides its own page. */
  shownOn?: string[];
  /** What changed, for Update post. */
  changes?: { lines: string[]; more: number };
  /** The last line. */
  note: string;
  confirm: string;
  /** "Schedule instead", offered from Publish. */
  alternative?: string;
}

interface ConfirmationInput {
  placeholders?: number;
  title: string;
  address: string;
  shownOn: string[];
  /** The Website's changes not live yet. */
  websiteChanges: number;
  /** When it goes live, for Schedule; the date it went live, for Update. */
  when?: string;
  since?: string;
  status: PostStatus;
  changes?: { lines: string[]; more: number };
}

/** What each confirmation says: where the post goes live, where it shows, and that the Website's changes aren't part of it. */
export function confirmationCopy(kind: Confirmation, input: ConfirmationInput): ConfirmationCopy {
  if (input.placeholders && ["publish", "schedule", "update"].includes(kind)) {
    const copy = confirmationCopy(kind, { ...input, placeholders: 0 });
    return { ...copy, title: `${copy.title} ${input.placeholders} ${input.placeholders === 1 ? "placeholder remains" : "placeholders remain"}.` };
  }
  switch (kind) {
    case "publish":
      return {
        title: "Publish this post now?",
        lead: { before: `“${input.title}” goes live straight away at `, address: input.address, after: "." },
        shownOn: input.shownOn,
        note: `Only this post goes live. ${websiteChangesNote(input.websiteChanges)}`,
        confirm: "Publish now",
        alternative: "Schedule instead",
      };
    case "schedule":
      return {
        title: "Schedule this post?",
        lead: { before: `“${input.title}” goes live on ${input.when ?? "the date you chose"} at `, address: input.address, after: "." },
        shownOn: input.shownOn,
        note: `Until then only you can see it. ${websiteChangesNote(input.websiteChanges)}`,
        confirm: "Schedule post",
      };
    case "update":
      return {
        title: input.status === "scheduled" ? "Update the scheduled post?" : "Update the live post?",
        lead: input.status === "scheduled"
          ? { before: "It still goes live on ", address: input.when ?? "its date", after: ", with your changes:" }
          : { before: "Readers of ", address: input.address, after: " see your changes straight away:" },
        ...(input.changes ? { changes: input.changes } : {}),
        note: `${input.status === "scheduled" ? "The date stays as it is." : `The publish date stays${input.since ? ` ${input.since}` : " as it is"}.`} Nothing else on your website changes.`,
        confirm: "Update post",
      };
    case "unpublish":
      return input.status === "scheduled" ? {
        title: "Unschedule this post?",
        lead: { before: "It won't go live at ", address: input.address, after: " and goes back to Draft. You can schedule or publish it again any time." },
        note: "Your words are kept.",
        confirm: "Unschedule",
      } : {
        title: "Unpublish this post?",
        lead: { before: "It comes off your blog at ", address: input.address, after: " and goes back to Draft. Readers won't find it. You can publish it again any time." },
        note: "Your words are kept.",
        confirm: "Unpublish",
      };
  }
}

export interface PostPanelCopy {
  /** The pill: Draft, Scheduled or Published. */
  badge: string;
  /** "Not on your blog yet", "Goes live Mon 12 Oct, 9:00 am", "Live since 21 Sep". */
  line: string;
  /** What this means for readers. */
  explain: string;
}

/** The "This post" box in the post panel. */
export function postPanelCopy(post: Pick<BlogPostEntry, "status" | "live" | "firstPublishedAt" | "hasChanges">, locale: Locale, pending: number, now = Date.now()): PostPanelCopy {
  if (post.status === "draft") return { badge: "Draft", line: "Not on your blog yet", explain: "A post goes live on its own, when you publish or schedule it. The website's Publish doesn't change it." };
  if (post.status === "scheduled") {
    return {
      badge: "Scheduled", line: `Goes live ${shortDate(post.live?.publishAt, locale, { time: true, weekday: true, now })}`,
      explain: post.hasChanges && pending > 0 ? `${pendingLine(pending)}. Choose Update post to put them in the scheduled post.` : "It goes live by itself on that date. The website's Publish doesn't change it.",
    };
  }
  const since = shortDate(post.live?.publishAt ?? post.firstPublishedAt ?? undefined, locale, { now });
  return {
    badge: "Published", line: since ? `Live since ${since}` : "Live",
    explain: post.hasChanges ? `${pending > 0 ? pendingLine(pending) : "Changes saved but not live yet"}. Readers see them when you choose Update post.` : "Readers see this version. The website's Publish doesn't change it.",
  };
}

/** The line at the foot of the post panel. */
export function saveLine(post: Pick<BlogPostEntry, "status" | "hasChanges">, saved: "saved" | "saving" | "unsaved", justSaved: boolean): string {
  if (saved === "saving") return "Saving…";
  if (saved === "unsaved") return "Editing…";
  const lead = justSaved ? "Saved just now" : "Saved";
  if (post.status === "draft") return `${lead} · not on your blog until you publish it`;
  if (post.status === "scheduled") return `${lead} · ${post.hasChanges ? "not in the scheduled post until you update" : "goes live on its date"}`;
  return `${lead} · ${post.hasChanges ? "not live until you update" : "live"}`;
}

/** The strip's own line next to the status pill: what only the owner can see, and the words. */
export function stripNote(post: Pick<BlogPostEntry, "status" | "hasChanges">): { dot: boolean; text: string } | null {
  if (post.status !== "draft" && post.hasChanges) return { dot: true, text: "Changes not live yet" };
  return null;
}

export const PUBLISHED_BAR_NOTE = "readers still see the published version";
